import { useMemo } from "react";
import type { ProductVariant, ProductSku } from "@/lib/supabase";

interface VariantSelectorProps {
  variants: ProductVariant[];
  skus: ProductSku[];
  selected: (string | null)[]; // one per variant index, in variants order
  onChange: (next: (string | null)[]) => void;
}

/**
 * For each variant (axis), for each option value: figure out if there is at least
 * one SKU that matches all already-selected OTHER axes AND uses this option value
 * with stock > 0. If not, mark the option as out of stock (still selectable but visibly dimmed).
 */
export default function VariantSelector({
  variants,
  skus,
  selected,
  onChange,
}: VariantSelectorProps) {
  const optionAvailability = useMemo(() => {
    // For each variant index → for each option → { available: boolean, anyExist: boolean }
    return variants.map((variant, vIdx) => {
      const map: Record<string, { inStock: boolean; exists: boolean }> = {};
      for (const opt of variant.options) {
        // Find SKUs whose options at vIdx === opt AND match other selected axes (if set)
        const matching = skus.filter((sku) => {
          const skuOpts = sku.options ?? [];
          if (skuOpts[vIdx] !== opt) return false;
          for (let i = 0; i < variants.length; i++) {
            if (i === vIdx) continue;
            const selVal = selected[i];
            if (selVal != null && skuOpts[i] !== selVal) return false;
          }
          return true;
        });
        const inStock = matching.some((s) => (s.stock ?? 0) > 0);
        map[opt] = { inStock, exists: matching.length > 0 };
      }
      return map;
    });
  }, [variants, skus, selected]);

  const handleSelect = (variantIndex: number, value: string) => {
    const next = [...selected];
    next[variantIndex] = next[variantIndex] === value ? null : value;
    onChange(next);
  };

  if (variants.length === 0) return null;

  return (
    <div className="space-y-5">
      {variants.map((variant, vIdx) => {
        const currentVal = selected[vIdx];
        return (
          <div key={variant.id}>
            <div className="flex items-baseline justify-between mb-2">
              <label className="text-[13px] font-medium text-foreground-900">
                {variant.name}
                {currentVal && (
                  <span className="ml-2 text-foreground-600 font-normal">
                    · {currentVal}
                  </span>
                )}
                {!currentVal && (
                  <span className="ml-2 text-primary-600 font-normal">
                    · escolha uma opção
                  </span>
                )}
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              {variant.options.map((opt) => {
                const info = optionAvailability[vIdx]?.[opt] ?? { inStock: true, exists: true };
                const isSelected = currentVal === opt;
                const isOut = !info.inStock;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => handleSelect(vIdx, opt)}
                    aria-pressed={isSelected}
                    className={`relative px-4 h-10 rounded-full text-[13px] font-medium transition-all cursor-pointer whitespace-nowrap border ${
                      isSelected
                        ? "bg-foreground-950 text-background-50 border-foreground-950"
                        : isOut
                        ? "bg-background-50 text-foreground-500 border-background-300/80 hover:border-foreground-400"
                        : "bg-background-50 text-foreground-900 border-background-300 hover:border-foreground-900"
                    }`}
                  >
                    {opt}
                    {isOut && !isSelected && (
                      <span className="absolute inset-0 rounded-full pointer-events-none">
                        <span className="absolute left-2 right-2 top-1/2 h-px bg-foreground-400 -rotate-6"></span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}